import { Compass } from "lucide-react";
import { ReactNode, useEffect, useState } from "react";

import { useOpenPlaygroundTourInNewTab } from "./hooks/useOpenPlaygroundTourInNewTab";

import { Button } from "@/components/ui/button";
import { Popover, PopoverAnchor, PopoverContent } from "@/components/ui/popover";
import useLocalStorageState, { LOCAL_STORAGE_KEYS } from "@/hooks/useLocalStorageState";
import { cn } from "@/lib/utils";
import { useUser } from "@/context/user-context";

interface PlaygroundTourFirstVisitProps {
  children: ReactNode;
}

/**
 * Wraps a tour trigger and shows a one-time popover nudge to first-time
 * users asking if they want to walk through the playground. Both Yes and
 * No write the visit timestamp so the popover never appears twice. Yes
 * additionally opens the standalone playground in a new tab with the tour
 * pre-armed.
 */
const PlaygroundTourFirstVisit = ({ children }: PlaygroundTourFirstVisitProps) => {
  const [visitedAt, setVisitedAt] = useLocalStorageState<string | null>(
    LOCAL_STORAGE_KEYS.TOUR_PLAYGROUND_VISITED,
    null,
  );
  const [dismissedAt, setDismissedAt] = useLocalStorageState<string | null>(
    LOCAL_STORAGE_KEYS.TOUR_PLAYGROUND_POPOVER_DISMISSED,
    null,
  );
  const { isNewUser } = useUser();

  // Snapshot the initial value so toggling the popover (or another tab
  // writing the flag) does not re-open it mid-session.
  const [open, setOpen] = useState(!visitedAt && !dismissedAt && isNewUser);
  const openTourInNewTab = useOpenPlaygroundTourInNewTab();

  useEffect(() => {
    if (!isNewUser) {
      setOpen(false);
    }
  }, [isNewUser]);

  const markVisited = () => setVisitedAt(new Date().toISOString());

  const handleAccept = () => {
    setDismissedAt(new Date().toISOString());
    markVisited();
    setOpen(false);
    openTourInNewTab();
  };

  const handleDismiss = () => {
    // Persist the popover dismissal separately so "No thanks" stops the
    // popover from reappearing without marking the full Playground tour as
    // visited — the auto-tour can still fire on first real Playground visit.
    setDismissedAt(new Date().toISOString());
    setOpen(false);
  };

  return (
    <Popover open={open}>
      {!visitedAt && (
        <PopoverAnchor className={cn(open && "rounded-lg bg-black/5")}>{children}</PopoverAnchor>
      )}
      <PopoverContent align="center" className="w-80">
        <div className="flex flex-col gap-3">
          <div className="flex items-start gap-2">
            <Compass className="mt-0.5 h-4 w-4 shrink-0 text-blue-500" />
            <div className="flex flex-col gap-1">
              <p className="text-sm font-semibold text-gray-900">New here?</p>
              <p className="text-xs leading-relaxed text-gray-600">
                Take a quick tour of the Playground to see how prompts, variables, and outputs fit
                together.
              </p>
            </div>
          </div>
          <div className="flex items-center justify-end gap-2">
            <Button variant="ghost" size="sm" onClick={handleDismiss}>
              No thanks
            </Button>
            <Button size="sm" onClick={handleAccept}>
              Show me
            </Button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default PlaygroundTourFirstVisit;
